import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supportedLanguages, type SupportedLanguage } from "@/i18n/config";

const OG_LOCALE_MAP: Record<SupportedLanguage, string> = {
  cs: "cs_CZ",
  en: "en_GB",
  de: "de_DE",
  sk: "sk_SK",
  hr: "hr_HR",
  it: "it_IT",
  nl: "nl_NL",
  et: "et_EE",
  lt: "lt_LT",
  lv: "lv_LV",
  hu: "hu_HU",
};

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
}

const BASE_URL = "https://ekokompakt.cz";

const SEOHead = ({ title, description, canonical, ogImage, ogType, noIndex, jsonLd }: SEOHeadProps) => {
  const location = useLocation();

  useEffect(() => {
    // Detect current language from pathname prefix
    const langMatch = location.pathname.match(/^\/(en|de|sk|hr|it|nl|et|lt|lv|hu)/);
    const currentLang = (langMatch ? langMatch[1] : "cs") as SupportedLanguage;
    const ogLocale = OG_LOCALE_MAP[currentLang];

    // Update <html lang> attribute
    document.documentElement.lang = currentLang;
    // Title
    document.title = title;

    // Meta tags
    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:url", canonical || BASE_URL, true);
    setMeta("og:type", ogType || "website", true);
    setMeta("og:locale", ogLocale, true);
    setMeta("og:image", ogImage || `${BASE_URL}/logo.png`, true);
    setMeta("twitter:card", ogImage ? "summary_large_image" : "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage || `${BASE_URL}/logo.png`);

    // og:locale:alternate for other languages
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    supportedLanguages.forEach((lang) => {
      if (OG_LOCALE_MAP[lang] !== ogLocale) {
        const altMeta = document.createElement("meta");
        altMeta.setAttribute("property", "og:locale:alternate");
        altMeta.setAttribute("content", OG_LOCALE_MAP[lang]);
        document.head.appendChild(altMeta);
      }
    });

    // Robots meta
    let robotsMeta = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!robotsMeta) {
      robotsMeta = document.createElement("meta") as HTMLMetaElement;
      robotsMeta.name = "robots";
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute("content", noIndex ? "noindex, nofollow" : "index, follow");

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = canonical || BASE_URL;

    // Hreflang tags
    document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
    const pathWithoutLang = location.pathname.replace(/^\/(en|de|sk|hr|it|nl|et|lt|lv|hu)/, "") || "/";
    supportedLanguages.forEach((lang) => {
      const hrefLink = document.createElement("link");
      hrefLink.rel = "alternate";
      hrefLink.hreflang = lang;
      hrefLink.href = `${BASE_URL}${lang === "cs" ? pathWithoutLang : `/${lang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`}`;
      hrefLink.setAttribute("data-hreflang", "true");
      document.head.appendChild(hrefLink);
    });
    // x-default → Czech
    const defaultLink = document.createElement("link");
    defaultLink.rel = "alternate";
    defaultLink.hreflang = "x-default";
    defaultLink.href = `${BASE_URL}${pathWithoutLang}`;
    defaultLink.setAttribute("data-hreflang", "true");
    document.head.appendChild(defaultLink);

    // JSON-LD
    document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-jsonld", "true");
        script.textContent = JSON.stringify(item);
        document.head.appendChild(script);
      });
    }

    return () => {
      document.querySelectorAll('script[data-seo-jsonld]').forEach(el => el.remove());
      document.querySelectorAll('link[data-hreflang]').forEach(el => el.remove());
      document.querySelectorAll('meta[property="og:locale:alternate"]').forEach(el => el.remove());
    };
  }, [title, description, canonical, ogImage, ogType, noIndex, jsonLd, location.pathname]);

  return null;
};

export default SEOHead;
